import { format, parseISO } from "date-fns"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatDuration } from "@/components/RouteStopView"
import { isStopLate, RouteStop } from "@/reducers/routes2gSlice"
import { stopCountLabel } from "./plannerLabels"

// The route's own vocabulary — a Slick route stop carries an action, not a TMS
// stop kind, so this doesn't reuse the chain dialog's labels.
const ACTION_LABEL_KEYS = {
  pickup: "planner.routeSegment.actions.pickup",
  delivery: "planner.routeSegment.actions.delivery",
  passthrough: "planner.routeSegment.actions.passthrough",
  customs: "planner.routeSegment.actions.customs",
  dropoff: "planner.routeSegment.actions.dropoff",
  scale: "planner.routeSegment.actions.scale",
} as const

const actionLabelKey = (action?: string) =>
  action && Object.prototype.hasOwnProperty.call(ACTION_LABEL_KEYS, action)
    ? ACTION_LABEL_KEYS[action as keyof typeof ACTION_LABEL_KEYS]
    : undefined

const parseTime = (value?: string): Date | null => {
  if (!value) return null
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const stopAddress = (stop: RouteStop): string =>
  stop.location.formattedAddress || stop.location.locality || "?"

export type RouteSegmentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The vehicle and driver this route belongs to. */
  rowLabel: string
  stops: RouteStop[]
}

/**
 * The stops behind one Slick route bar segment.
 *
 * The route bar is the only bar on the planner with no TMS order behind it —
 * it is stops sent to the driver — so clicking it can't open an order. It opens
 * what it does know: which stops this segment spans, when each is due, and
 * which of them the driver is late to.
 */
const RouteSegmentDialog = ({
  open,
  onOpenChange,
  rowLabel,
  stops,
}: RouteSegmentDialogProps) => {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t("planner.routeSegment.title", {
              stops: stopCountLabel(stops.length),
            })}
          </DialogTitle>
          <DialogDescription>{rowLabel}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {stops.map((stop, index) => {
            const scheduled = parseTime(stop.scheduled_arrival_time)
            const estimated =
              parseTime(stop.estimated_arrival_time_with_tacho) ??
              parseTime(stop.estimated_arrival_time)
            const isLate = isStopLate(stop)
            const labelKey = actionLabelKey(stop.action)
            const delaySeconds =
              estimated && scheduled
                ? Math.floor((estimated.getTime() - scheduled.getTime()) / 1000)
                : null

            return (
              <div
                key={stop.id ?? `${stop.msgId ?? "stop"}-${index.toString()}`}
                className="flex items-center justify-between gap-2 rounded border px-2.5 py-1.5 text-xs"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{stopAddress(stop)}</p>
                  <p className="truncate text-muted-foreground">
                    {labelKey ? t(labelKey) : t("planner.routeSegment.stop")}
                    {stop.ref && ` • ${stop.ref}`}
                  </p>
                </div>
                <div className="shrink-0 text-right text-muted-foreground">
                  <p>
                    {scheduled
                      ? format(scheduled, "dd.MM HH:mm")
                      : t("planner.common.noDate")}
                  </p>
                  {isLate && (
                    <p className="text-rose-500">
                      {t("planner.routeSegment.late")}
                    </p>
                  )}
                  {isLate && estimated && (
                    <p className="text-rose-500">
                      {t("planner.common.eta", {
                        time: format(estimated, "HH:mm"),
                      })}
                      {delaySeconds !== null &&
                        delaySeconds > 0 &&
                        ` (${formatDuration(delaySeconds)})`}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RouteSegmentDialog
